/**
 * AI text remix (remix_all_text / inline editor): allowed for admin/beta, or any
 * account with remaining actions budget that is not on a pre-2025 plan — including free.
 */
export default function canUseAiRemixText({
  currentUser,
  website,
  subscription,
  account,
  usage,
  limit,
} = {}) {
  if (currentUser?.admin_type || currentUser?.beta === true) {
    return true;
  }

  const accountPlan = String(
    subscription?.plan_type ||
      (typeof document !== "undefined"
        ? document.getElementById("upgrade")?.getAttribute("data-plan-type")
        : null) ||
      ""
  );
  const websitePlan = String(
    website?.plan_type ||
      (typeof document !== "undefined"
        ? document.getElementById("upgrade")?.getAttribute("data-website-plan")
        : null) ||
      ""
  );

  if (isPre2025Plan(accountPlan) || isPre2025Plan(websitePlan)) {
    return false;
  }

  if (
    actionsBudgetExhausted({
      account,
      usage,
      limit,
      subscription,
    })
  ) {
    return false;
  }

  // Free accounts with an actions budget (and paid 2025/2026 plans) can use
  // the remix text editor / builder chat / custom templates.
  return true;
}

// Free plans get +10 past the included meter so a 100-action asset import can
// still complete surrounding builder-chat turns before the hard gate.
export const FREE_ACTIONS_HARD_GATE_BUFFER = 10;

export function actionsBudgetHardLimit({ account, limit, subscription } = {}) {
  const resolvedLimit = Number(
    limit ?? account?.billable_actions_limit ?? account?.actions_limit ?? 0
  );
  if (!Number.isFinite(resolvedLimit) || resolvedLimit <= 0) {
    return 0;
  }

  const accountPlan = String(
    subscription?.plan_type ||
      (typeof document !== "undefined"
        ? document.getElementById("upgrade")?.getAttribute("data-plan-type")
        : null) ||
      ""
  );
  if (accountPlan === "free") {
    return resolvedLimit + FREE_ACTIONS_HARD_GATE_BUFFER;
  }

  return resolvedLimit;
}

export function actionsBudgetExhausted({
  account,
  usage,
  limit,
  subscription,
} = {}) {
  const hardLimit = actionsBudgetHardLimit({ account, limit, subscription });
  if (!hardLimit) {
    return false;
  }

  const resolvedUsage = Number(
    usage ?? account?.billable_actions_usage ?? account?.actions_usage ?? 0
  );
  if (!Number.isFinite(resolvedUsage)) {
    return false;
  }

  return resolvedUsage >= hardLimit;
}

/**
 * Builder chat / custom templates. Available on any website when the account
 * plan includes AI actions and usage remains — including free-tier sites on
 * paid plans so customers can onboard new sites. Free accounts may use it
 * while lifetime actions remain (hard gate includes free buffer).
 */
export function canUseAiRemixBuilderChat({
  currentUser,
  website,
  subscription,
  account,
  usage,
  limit,
} = {}) {
  if (currentUser?.admin_type || currentUser?.beta === true) {
    return true;
  }

  return canUseAiRemixText({
    currentUser,
    website,
    subscription,
    account,
    usage,
    limit,
  });
}

function isPre2025Plan(planType) {
  const plan = String(planType || "");
  if (!plan || plan === "free") return false;
  return (
    plan.includes("2024") ||
    plan.includes("2023") ||
    plan.includes("2022") ||
    plan.includes("2021") ||
    plan.includes("2020") ||
    plan.includes("2019")
  );
}

/**
 * Custom templates / asset imports. Same eligibility as builder chat.
 */
export function canUseAiRemixCustomTemplates(args = {}) {
  return canUseAiRemixBuilderChat(args);
}

/**
 * Paid Remix AI features: premium-template remix, duplicate+remix.
 * Free / non-premium / pre-2025 remain gated. Custom templates use
 * canUseAiRemixCustomTemplates; builder chat uses canUseAiRemixBuilderChat.
 */
export function canUseAiRemixPaid({ currentUser, website, subscription } = {}) {
  if (currentUser?.admin_type || currentUser?.beta === true) {
    return true;
  }
  if (website?.premium !== true) {
    return false;
  }

  const accountPlan = String(
    subscription?.plan_type ||
      (typeof document !== "undefined"
        ? document.getElementById("upgrade")?.getAttribute("data-plan-type")
        : null) ||
      ""
  );
  const websitePlan = String(
    website?.plan_type ||
      (typeof document !== "undefined"
        ? document.getElementById("upgrade")?.getAttribute("data-website-plan")
        : null) ||
      ""
  );
  if (accountPlan === "free") {
    return false;
  }
  if (isPre2025Plan(accountPlan) || isPre2025Plan(websitePlan)) {
    return false;
  }

  if (
    websitePlan.includes("2026") ||
    websitePlan.includes("2025") ||
    accountPlan.includes("2026") ||
    accountPlan.includes("2025")
  ) {
    return true;
  }

  if (accountPlan === "2026_personal") {
    return true;
  }

  return false;
}

/**
 * Template AI Remix toggle. Available for any template while the account has
 * remaining AI actions (including free accounts / free-tier sites).
 */
export function canUseAiRemixTemplate({
  currentUser,
  website,
  subscription,
  account,
  template,
} = {}) {
  if (!template) {
    return false;
  }

  if (canUseAiRemixPaid({ currentUser, website, subscription })) {
    return true;
  }

  return canUseAiRemixText({
    currentUser,
    website,
    subscription,
    account,
  });
}

/**
 * When Remix AI is gated, which upgrade gate to show.
 */
export function remixAiUpgradePlan({ subscription, website } = {}) {
  const accountPlan =
    subscription?.plan_type ||
    (typeof document !== "undefined"
      ? document.getElementById("upgrade")?.getAttribute("data-plan-type")
      : null) ||
    "free";
  const websitePlan =
    website?.plan_type ||
    (typeof document !== "undefined"
      ? document.getElementById("upgrade")?.getAttribute("data-website-plan")
      : null);

  const websiteIsPremium =
    website != null
      ? website.premium === true
      : typeof document !== "undefined" &&
        document
          .getElementById("upgrade")
          ?.getAttribute("data-website-premium") === "true";

  if (
    String(accountPlan || "").includes("2024") ||
    String(websitePlan || "").includes("2024")
  ) {
    return "remix";
  }

  // Remix AI isn't gated to Pro specifically, so free accounts / free sites
  // are pointed at the entry-level paid plan gate instead of Pro.
  if (String(accountPlan || "") === "free" || !websiteIsPremium) {
    return "paid";
  }

  return "pro";
}
